import { Button, Dialog, Input } from '@artosera/ui';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { comptabiliteApi, useApercuFec } from '../../api/comptabilite.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { enregistrerFichier } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { Field } from '../Field.js';
import styles from './AccesApiPage.module.css';
export function ExportFec({ galerie }: { galerie: string }): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const annee = new Date().getFullYear() - 1;
  const [ouvert, setOuvert] = useState(false);
  const [du, setDu] = useState(`${annee}-01-01`);
  const [au, setAu] = useState(`${annee}-12-31`);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const valide = /^\d{4}-\d{2}-\d{2}$/.test(du) && /^\d{4}-\d{2}-\d{2}$/.test(au) && du <= au;
  const apercu = useApercuFec(galerie, du, au, ouvert && valide);
  const telecharger = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const fichier = await dansLaPorteeCourante((appel) =>
        comptabiliteApi.telechargerFec(galerie, du, au, appel),
      );
      enregistrerFichier(fichier, 'fec.txt');
    } catch (e) {
      if (!estPorteeObsolete(e)) setErreur(estApiError(e) ? e.message : t('accesApi.erreurReseau'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setErreur(null);
          setOuvert(true);
        }}
      >
        {t('fec.bouton')}
      </Button>
      <Dialog
        open={ouvert}
        onOpenChange={setOuvert}
        title={t('fec.bouton')}
        description={t('fec.portee')}
        closeLabel={t('commun:fermer')}
        footer={
          <Button
            disabled={
              !valide || !apercu.data?.nomFichier || apercu.isFetching || apercu.isError || enCours
            }
            onClick={() => void telecharger()}
          >
            {t('fec.telecharger')}
          </Button>
        }
      >
        <div className={styles.plan}>
          <Field label={t('fec.du')} htmlFor="fec-du">
            <Input
              id="fec-du"
              type="date"
              value={du}
              disabled={enCours}
              onChange={(e) => setDu(e.target.value)}
            />
          </Field>
          <Field label={t('fec.au')} htmlFor="fec-au">
            <Input
              id="fec-au"
              type="date"
              value={au}
              disabled={enCours}
              onChange={(e) => setAu(e.target.value)}
            />
          </Field>
        </div>
        {!valide && <p role="alert">{t('fec.periodeInvalide')}</p>}
        {valide && apercu.isPending && <p role="status">{t('commun:chargement')}</p>}
        {apercu.isError && (
          <p role="alert">
            {estApiError(apercu.error) ? apercu.error.message : t('accesApi.erreurReseau')}
          </p>
        )}
        {valide && apercu.data && (
          <>
            <p>
              {t('fec.apercu', { pieces: apercu.data.pieces, reglements: apercu.data.reglements })}
            </p>
            {apercu.data.pieces === 0 && apercu.data.reglements === 0 && <p>{t('fec.vide')}</p>}
            <ul>
              {apercu.data.exclues.map((e, i) => (
                <li key={`${e.numero}-${i}`}>{t(`fec.exclue.${e.motif}`, { numero: e.numero })}</li>
              ))}
            </ul>
            {apercu.data.avertissements.includes('siren_absent') && (
              <p role="alert">{t('fec.siren')}</p>
            )}
          </>
        )}
        {erreur && <p role="alert">{erreur}</p>}
      </Dialog>
    </>
  );
}
